"use client";
import * as prismic from "@prismicio/client";
import { Heading } from "@/components/Heading";
import { useEffect, useState } from "react";
import Image from "next/image";
import { PrismicNextImage } from "@prismicio/next";
import { PrismicRichText } from "@/components/PrismicRichText";
import clsx from "clsx";
import Link from "next/link";
import { Button } from "@/components/Button";

export default function PageOpenerWithSections({ slice }) {
  const titleColour = slice.primary.title_colour;
  const titleShadow = slice.primary.title_shadow;
  const bgColour = slice.primary.background_color;
  const imageAlign = slice.primary.image_align;
  const size = slice.primary.section_size;
  const [scale, setScale] = useState(1);
  const links = slice.primary.links;

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const newScale = 1 + scrollY * 0.0005; // Adjust the zoom factor as needed
      setScale(newScale);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const heightClass =
    size === "large"
      ? "h-[310px] md:h-[810px]"
      : size === "medium"
        ? "h-[310px] md:h-[710px]"
        : "h-[310px]";

  return (
    <>
      <link rel="preload" href={slice.primary.image.url} as="image" />

      <div
        className={`relative w-screen  -mt-[100px] overflow-hidden flex items-center justify-center`}
        style={{ backgroundColor: bgColour }}
      >
        <Image
          src={slice.primary.image.url}
          alt={slice.primary.image.alt}
          sizes="(max-width: 600px) 90vw, (max-width: 1200px) 60vw, 50vw"
          fill
          style={{
            objectFit: "cover",
            objectPosition: `${imageAlign}`,
            transform: `scale(${scale})`,
          }}
          loading="eager"
        />
        <div className="flex flex-col items-center justify-center w-full max-w-7xl mx-auto py-40 ">
          <div
            className="flex left-0 right-0 mx-auto z-10 w-full max-w-2xl text-center items-center mt-20"
            style={{
              color: titleColour,
              textShadow: titleShadow ? "2px 1px 4px #0000002e" : "none",
            }}
          >
            <Heading
              as="h1"
              size="5xl"
              className={`mb-7 mt-12 first:mt-0 last:mb-0 mx-auto`}
            >
              {prismic.asText(slice.primary.title)}
            </Heading>
          </div>
          <div className="flex flex-col mx-auto w-full md:flex-row gap-8 max-w-7xl justify-between items-stretch mt-14 px-10">
            {links.map((item, index) => {
              const content = (
                <div
                  className="relative group w-full flex flex-col md:flex-col lg:flex-row h-full flex-grow justify-grow text-center mb-0 md:mb-0 p-0 md:p-6 bg-white/30 hover:bg-white/60 transition-all duration-300 ease-in-out"
                  style={{ backgroundColor: item.colour }}
                >
                  <div className="w-full md-full lg:w-5/12">
                    <PrismicNextImage
                      field={item.image}
                      alt={item.image.alt}
                      loading="lazy"
                      className="w-full mx-auto overflow-hidden p-6 md:p-0 group-hover:scale-105 transition-all duration-300 ease-in-out"
                    />
                  </div>
                  <div className="relative flex flex-col text-center mt-10 lg:mt-0 md:text-left w-full lg:w-7/12 px-6 lg:px-6 text-xl justify-between">
                    <Heading as="h2" size="3xl">
                      {prismic.asText(item.title)}
                    </Heading>

                    <PrismicRichText
                      field={item.description}
                      components={{
                        paragraph: ({ children }) => (
                          <p
                            className={clsx(
                              "text-sm/6 text-line mt-4 mb-2 px-6 md:px-2 mx-auto",
                              "text-slate-600"
                            )}
                          >
                            {children}
                          </p>
                        ),
                      }}
                    />

                    {item.link && item.link.url && (
                      <Button
                        field={item.link}
                        variant="solid"
                        color={item.link?.variant?.toLowerCase()}
                        className="mt-20 mb-10 md:mb-0"
                      >
                        {item.link.text}
                      </Button>
                    )}
                  </div>
                </div>
              );

              // Conditionally wrap with Link if item.link is set

              // If there's no link, simply return the content inside a div with a key.
              return <div key={index}>{content}</div>;
            })}
          </div>
        </div>
      </div>
    </>
  );
}
